import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  storeAssetFormSchema,
  type StoreAssetFormData,
} from '@/lib/validations/asset'
import {
  fetchBrandModelOptions,
  fetchCpuOptions,
  fetchFunctionOptions,
  fetchOsVersionOptions,
  fetchProcessorOptions,
  fetchSpecificCategoryOptions,
  filterSpecificCategoryOptions,
  parseBrandMachineTypes,
  withCurrentOption,
  type BrandOption,
  type NamedOption,
  type StoreAssetOptionLists,
} from '@/lib/services/storeService'
import { toDropdownLabel } from '@/lib/utils/displayLabel'

const ramSortValue = (name: string): number => {
  const match = name.match(/(\d+)/)
  return match ? Number.parseInt(match[1], 10) : 0
}

const diskSortValue = (name: string): number => {
  const match = name.match(/(\d+)/)
  return match ? Number.parseInt(match[1], 10) : 0
}

interface StoreAssetFormProps {
  mode: 'create' | 'edit'
  defaultValues: StoreAssetFormData
  audited?: boolean
  brandOptions: BrandOption[]
  optionLists?: StoreAssetOptionLists
  onSubmit: (data: StoreAssetFormData) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

function RequiredMark() {
  return (
    <Text as="span" color="red.500">
      {' '}
      *
    </Text>
  )
}

function FieldRow({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
      <FormLabel pt={2} fontWeight="medium" mb={0}>
        {label}
        {required ? <RequiredMark /> : null}
      </FormLabel>
      {children}
    </Grid>
  )
}

export function StoreAssetForm({
  mode,
  defaultValues,
  audited = false,
  brandOptions,
  optionLists,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: StoreAssetFormProps) {
  const isLocked = audited
  const hasPreloadedOptions = optionLists != null
  const [optionsLoading, setOptionsLoading] = useState(!hasPreloadedOptions)
  const [specificCategories, setSpecificCategories] = useState<NamedOption[]>(
    optionLists?.specificCategories ?? []
  )
  const [cpuOptions, setCpuOptions] = useState<NamedOption[]>(optionLists?.cpuOptions ?? [])
  const [osVersionOptions, setOsVersionOptions] = useState<NamedOption[]>(
    optionLists?.osVersionOptions ?? []
  )
  const [processorOptions, setProcessorOptions] = useState<NamedOption[]>(
    optionLists?.processorOptions ?? []
  )
  const [machineTypes, setMachineTypes] = useState<Array<{ id: number; name: string }>>([])
  const [brandModels, setBrandModels] = useState<NamedOption[]>([])
  const [functions, setFunctions] = useState<Array<{ name: string }>>([])
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<StoreAssetFormData>({
    resolver: zodResolver(storeAssetFormSchema),
    defaultValues,
  })

  const brandName = watch('brand_name')
  const categoryName = watch('category_name')
  const swProvider = watch('sw_provider') ?? ''
  const ram = watch('ram') ?? ''
  const diskSpace = watch('disk_space') ?? ''
  const msOffice = watch('ms_office') ?? ''
  const functionValue = watch('function') ?? ''
  const modelValue = watch('model') ?? ''
  const osVersion = watch('os_version') ?? ''
  const processorValue = watch('processor') ?? ''
  const cpuValue = watch('cpu') ?? ''

  useEffect(() => {
    reset(defaultValues)
    const brand = brandOptions.find((item) => item.name === defaultValues.brand_name)
    const types = brand ? parseBrandMachineTypes(brand.categories) : []
    setMachineTypes(types)
    const category = types.find((item) => item.name === defaultValues.category_name)
    setSelectedBrandId(brand?.id ?? null)
    setSelectedCategoryId(category?.id ?? null)
  }, [defaultValues, brandOptions, reset])

  useEffect(() => {
    if (!optionLists) return
    setSpecificCategories(optionLists.specificCategories)
    setCpuOptions(optionLists.cpuOptions)
    setOsVersionOptions(optionLists.osVersionOptions)
    setProcessorOptions(optionLists.processorOptions)
    setOptionsLoading(false)
  }, [optionLists])

  useEffect(() => {
    if (optionLists) return

    let cancelled = false
    let pending = 4

    const finishOne = () => {
      pending -= 1
      if (pending <= 0 && !cancelled) {
        setOptionsLoading(false)
      }
    }

    setOptionsLoading(true)

    fetchSpecificCategoryOptions()
      .then((rows) => {
        if (!cancelled) setSpecificCategories(rows)
      })
      .catch(() => undefined)
      .finally(finishOne)

    fetchCpuOptions()
      .then((rows) => {
        if (!cancelled) setCpuOptions(rows)
      })
      .catch(() => undefined)
      .finally(finishOne)

    fetchOsVersionOptions()
      .then((rows) => {
        if (!cancelled) setOsVersionOptions(rows)
      })
      .catch(() => undefined)
      .finally(finishOne)

    fetchProcessorOptions()
      .then((rows) => {
        if (!cancelled) setProcessorOptions(rows)
      })
      .catch(() => undefined)
      .finally(finishOne)

    return () => {
      cancelled = true
    }
  }, [optionLists])

  const syncBrandSelection = useCallback(
    (nextBrandName: string, nextCategoryName = '') => {
      const brand = brandOptions.find((item) => item.name === nextBrandName)
      const types = brand ? parseBrandMachineTypes(brand.categories) : []
      setMachineTypes(types)
      const category = types.find((item) => item.name === nextCategoryName)
      setSelectedBrandId(brand?.id ?? null)
      setSelectedCategoryId(category?.id ?? null)
    },
    [brandOptions]
  )

  useEffect(() => {
    let cancelled = false
    const loadDependent = async () => {
      if (selectedBrandId && selectedCategoryId) {
        try {
          const models = await fetchBrandModelOptions(selectedBrandId, selectedCategoryId)
          if (!cancelled) setBrandModels(models)
        } catch {
          if (!cancelled) setBrandModels([])
        }
      } else if (!cancelled) {
        setBrandModels([])
      }

      if (selectedCategoryId) {
        try {
          const rows = await fetchFunctionOptions(selectedCategoryId)
          if (!cancelled) setFunctions(rows)
        } catch {
          if (!cancelled) setFunctions([])
        }
      } else if (!cancelled) {
        setFunctions([])
      }
    }
    void loadDependent()
    return () => {
      cancelled = true
    }
  }, [selectedBrandId, selectedCategoryId])

  const swProviderOptions = useMemo(
    () => withCurrentOption(filterSpecificCategoryOptions(specificCategories, 'SW Provider'), swProvider),
    [specificCategories, swProvider]
  )

  const ramOptions = useMemo(
    () =>
      withCurrentOption(filterSpecificCategoryOptions(specificCategories, 'RAM'), ram).sort(
        (a, b) => ramSortValue(a.name) - ramSortValue(b.name)
      ),
    [specificCategories, ram]
  )

  const diskSpaceOptions = useMemo(
    () =>
      withCurrentOption(filterSpecificCategoryOptions(specificCategories, 'Disk Space'), diskSpace).sort(
        (a, b) => diskSortValue(a.name) - diskSortValue(b.name)
      ),
    [specificCategories, diskSpace]
  )

  const msOfficeOptions = useMemo(
    () => withCurrentOption(filterSpecificCategoryOptions(specificCategories, 'MS Office'), msOffice),
    [specificCategories, msOffice]
  )

  const functionOptions = useMemo(() => {
    const options = [...functions]
    const current = functionValue.trim()
    if (current && !options.some((item) => item.name === current)) {
      options.unshift({ name: current })
    }
    return options.sort((a, b) => a.name.localeCompare(b.name))
  }, [functions, functionValue])

  const modelOptions = useMemo(() => {
    const options = [...brandModels]
    const current = modelValue.trim()
    if (current && !options.some((item) => item.name === current)) {
      options.unshift({ id: `current-${current}`, name: current })
    }
    return options.sort((a, b) => a.name.localeCompare(b.name))
  }, [brandModels, modelValue])

  const handleBrandChange = (nextBrandName: string) => {
    setValue('brand_name', nextBrandName, { shouldValidate: true })
    setValue('category_name', '', { shouldValidate: true })
    setValue('model', '', { shouldValidate: true })
    setValue('function', '', { shouldValidate: true })
    syncBrandSelection(nextBrandName)
  }

  const handleCategoryChange = (nextCategoryName: string) => {
    setValue('category_name', nextCategoryName, { shouldValidate: true })
    setValue('model', '', { shouldValidate: true })
    setValue('function', '', { shouldValidate: true })
    const brand = brandOptions.find((item) => item.name === brandName)
    const types = brand ? parseBrandMachineTypes(brand.categories) : machineTypes
    const category = types.find((item) => item.name === nextCategoryName)
    setSelectedCategoryId(category?.id ?? null)
  }

  const handleTagBlur = (value: string) => {
    const trimmed = value.trim()
    if (trimmed) {
      setValue('tag_number', trimmed.toUpperCase(), { shouldValidate: true })
    }
  }

  if (optionsLoading) {
    return (
      <VStack py={12} spacing={4}>
        <Spinner size="lg" />
        <Text color="gray.500">Loading form options…</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={5} align="stretch">
        {isLocked && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            This asset has been audited and can no longer be edited.
          </Alert>
        )}

        <Divider />
        <Text fontSize="md" fontWeight="semibold">
          Asset Identity
        </Text>

        <FieldRow label="Brand" required>
          <FormControl isInvalid={!!errors.brand_name}>
            <Select
              placeholder="Select brand"
              value={brandName}
              onChange={(e) => handleBrandChange(e.target.value)}
              isDisabled={isLocked}
            >
              {brandOptions.map((b) => (
                <option key={b.id} value={b.name}>
                  {toDropdownLabel(b.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.brand_name?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Machine Type" required>
          <FormControl isInvalid={!!errors.category_name}>
            <Select
              placeholder="Select machine type"
              value={categoryName}
              onChange={(e) => handleCategoryChange(e.target.value)}
              isDisabled={isLocked || !brandName}
            >
              {machineTypes.map((c) => (
                <option key={c.id} value={c.name}>
                  {toDropdownLabel(c.name)}
                </option>
              ))}
            </Select>
            {!brandName && <FormHelperText>Select a brand first</FormHelperText>}
            <FormErrorMessage>{errors.category_name?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Model" required>
          <FormControl isInvalid={!!errors.model}>
            <Select
              placeholder="Select model"
              {...register('model')}
              isDisabled={isLocked || !brandName || !categoryName}
            >
              {modelOptions.map((m) => (
                <option key={m.id} value={m.name}>
                  {toDropdownLabel(m.name)}
                </option>
              ))}
            </Select>
            {brandName && !categoryName && <FormHelperText>Select a machine type first</FormHelperText>}
            <FormErrorMessage>{errors.model?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Serial Number" required>
          <FormControl isInvalid={!!errors.serial_number}>
            <Input
              {...register('serial_number')}
              placeholder="e.g., SN123456789"
              isReadOnly={isLocked}
            />
            <FormErrorMessage>{errors.serial_number?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Tag Number">
          <FormControl isInvalid={!!errors.tag_number}>
            <Input
              {...register('tag_number', {
                onBlur: (e) => handleTagBlur(e.target.value),
              })}
              placeholder="e.g., ABC123"
              isReadOnly={isLocked}
            />
            <FormErrorMessage>{errors.tag_number?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Function" required>
          <FormControl isInvalid={!!errors.function}>
            <Select
              placeholder="Select function"
              {...register('function')}
              isDisabled={isLocked || !categoryName}
            >
              {functionOptions.map((f) => (
                <option key={f.name} value={f.name}>
                  {toDropdownLabel(f.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.function?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Manufacturer">
          <FormControl isInvalid={!!errors.manufacturer}>
            <Input
              {...register('manufacturer')}
              placeholder="e.g., Dell Inc."
              isReadOnly={isLocked}
            />
            <FormErrorMessage>{errors.manufacturer?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">
          Dates &amp; Software
        </Text>

        <FieldRow label="Date Deployed">
          <FormControl isInvalid={!!errors.warranty_date}>
            <Input type="date" {...register('warranty_date')} isReadOnly={isLocked} />
            <FormErrorMessage>{errors.warranty_date?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="Date Pulled Out">
          <FormControl isInvalid={!!errors.order_received_date}>
            <Input type="date" {...register('order_received_date')} isReadOnly={isLocked} />
            <FormErrorMessage>{errors.order_received_date?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="SW Provider">
          <FormControl isInvalid={!!errors.sw_provider}>
            <Select placeholder="Select SW provider" {...register('sw_provider')} isDisabled={isLocked}>
              {swProviderOptions.map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.sw_provider?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">
          Specifications
        </Text>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <FormControl isInvalid={!!errors.ram}>
            <FormLabel fontWeight="medium">RAM</FormLabel>
            <Select placeholder="Select RAM" {...register('ram')} isDisabled={isLocked}>
              {ramOptions.map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.ram?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.disk_space}>
            <FormLabel fontWeight="medium">Disk Space</FormLabel>
            <Select placeholder="Select disk space" {...register('disk_space')} isDisabled={isLocked}>
              {diskSpaceOptions.map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.disk_space?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.os_version}>
            <FormLabel fontWeight="medium">OS Version</FormLabel>
            <Select placeholder="Select OS version" {...register('os_version')} isDisabled={isLocked}>
              {withCurrentOption(osVersionOptions, osVersion).map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.os_version?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.ms_office}>
            <FormLabel fontWeight="medium">MS Office</FormLabel>
            <Select placeholder="Select MS Office" {...register('ms_office')} isDisabled={isLocked}>
              {msOfficeOptions.map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.ms_office?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.processor}>
            <FormLabel fontWeight="medium">Processor</FormLabel>
            <Select placeholder="Select processor" {...register('processor')} isDisabled={isLocked}>
              {withCurrentOption(processorOptions, processorValue).map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.processor?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.cpu}>
            <FormLabel fontWeight="medium">CPU</FormLabel>
            <Select placeholder="Select CPU" {...register('cpu')} isDisabled={isLocked}>
              {withCurrentOption(cpuOptions, cpuValue).map((o) => (
                <option key={o.id} value={o.name}>
                  {toDropdownLabel(o.name)}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.cpu?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">
          Network
        </Text>

        <FieldRow label="IP Address">
          <FormControl isInvalid={!!errors.ip_address}>
            <Input
              {...register('ip_address')}
              placeholder="e.g., 192.168.1.100"
              isReadOnly={isLocked}
            />
            <FormErrorMessage>{errors.ip_address?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <FieldRow label="MAC Address">
          <FormControl isInvalid={!!errors.mac_address}>
            <Input
              {...register('mac_address')}
              placeholder="e.g., 00:1A:2B:3C:4D:5E"
              isReadOnly={isLocked}
            />
            <FormErrorMessage>{errors.mac_address?.message}</FormErrorMessage>
          </FormControl>
        </FieldRow>

        <HStack justify="flex-end" pt={4} spacing={3}>
          <Button variant="ghost" onClick={onCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          {!isLocked && (
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
              {mode === 'edit' ? 'Save Changes' : 'Add Asset'}
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  )
}
